/**
 * Client Matcher Step — Matches email sender to existing clients
 *
 * Searches contacts by:
 * 1. Exact email match
 * 2. Domain match (company email domain)
 * 3. Company name match (if suggested by classifier)
 *
 * Config:
 * - senderEmail: string — Sender email address
 * - senderDomain: string — Sender email domain
 * - suggestedCompanyName?: string — Company name suggested by classifier
 */

import type { EngineStep, EngineExecutionContext, StepExecutionResult, StepExecutor } from '../types';

export interface ClientMatchResult {
  found: boolean;
  contactId?: string;
  contactName?: string;
  company?: string;
  matchType?: 'email' | 'domain' | 'company_name';
  matchConfidence?: number;
}

export const clientMatcherStep: StepExecutor = {
  type: 'client_match',
  async execute(step: EngineStep, context: EngineExecutionContext): Promise<StepExecutionResult> {
    try {
      const senderEmail = (step.config.senderEmail as string) || '';
      const senderDomain = (step.config.senderDomain as string) || '';
      const suggestedCompanyName = step.config.suggestedCompanyName as string | undefined;

      if (!senderEmail) {
        return { success: false, error: 'senderEmail is required' };
      }

      // Get hub store for contact lookup
      const { useHubStore } = await import('../../../stores/hubStore');
      const hubStore = useHubStore.getState();

      // Ensure data is loaded
      if (!hubStore.initialized) {
        await hubStore.loadFromServer();
      }

      const contacts = hubStore.contacts;
      let matchedContact: ClientMatchResult = { found: false };

      // 1. Try exact email match
      for (const contact of contacts) {
        const contactEmail = contact.handles?.email?.toLowerCase();
        if (contactEmail && contactEmail === senderEmail.toLowerCase()) {
          matchedContact = {
            found: true,
            contactId: contact.id,
            contactName: contact.displayName,
            company: contact.company,
            matchType: 'email',
            matchConfidence: 1.0,
          };
          break;
        }
      }

      // 2. Try domain match (if no exact match)
      if (!matchedContact.found && senderDomain) {
        // Skip common email providers
        const commonDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com', 'libero.it', 'virgilio.it', 'alice.it', 'tin.it', 'fastwebnet.it'];
        if (!commonDomains.includes(senderDomain.toLowerCase())) {
          for (const contact of contacts) {
            const contactEmail = contact.handles?.email?.toLowerCase() || '';
            const contactDomain = contactEmail.split('@')[1];
            if (contactDomain === senderDomain.toLowerCase()) {
              matchedContact = {
                found: true,
                contactId: contact.id,
                contactName: contact.displayName,
                company: contact.company,
                matchType: 'domain',
                matchConfidence: 0.8,
              };
              break;
            }
          }
        }
      }

      // 3. Try company name match (if no domain match)
      if (!matchedContact.found && suggestedCompanyName) {
        const normalizedSuggestion = suggestedCompanyName.toLowerCase().trim();
        for (const contact of contacts) {
          const contactCompany = (contact.company || '').toLowerCase().trim();
          if (contactCompany && (
            contactCompany.includes(normalizedSuggestion) ||
            normalizedSuggestion.includes(contactCompany)
          )) {
            matchedContact = {
              found: true,
              contactId: contact.id,
              contactName: contact.displayName,
              company: contact.company,
              matchType: 'company_name',
              matchConfidence: 0.6,
            };
            break;
          }
        }
      }

      return {
        success: true,
        output: {
          clientMatch: matchedContact,
          clientFound: matchedContact.found,
          clientId: matchedContact.contactId,
          clientName: matchedContact.contactName,
          clientCompany: matchedContact.company,
        },
      };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Client matching failed',
      };
    }
  },
};
